/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn, SelectItem, Select as SelectNextUI, SelectProps } from '@nextui-org/react';
import React from 'react';

import { Asterisk, X } from '@phosphor-icons/react';
import Text from '../Text';

export interface IOptions {
  label: string;
  value: string | number;
  description?: string;
}

export interface SelectCustomProps {
  options: IOptions[];
  label?: string;
  required?: boolean;
  selectionMode?: 'none' | 'single' | 'multiple';
  size?: 'lg' | 'sm' | 'md' | undefined;
  radius?: 'none' | 'lg' | 'sm' | 'md' | 'full' | undefined;
  id?: string;
  name?: string;
  selectedKeys?: any;
  value?: any;
  onSelectionChange?: any;
  onChange?: any;
  isInvalid?: boolean;
  errorMessage?: string;
  placeholder?: string;
}

const Select: React.FC<SelectCustomProps & Partial<SelectProps>> = ({
  label,
  required,
  selectionMode,
  options,
  selectedKeys,
  isInvalid,
  onSelectionChange,
  onChange,
  id,
  name,
  value,
  radius,
  placeholder,
  size,
  errorMessage,
  ...props
}) => {
  return (
    <div>
      {label && (
        <div className='mb-[6px] flex gap-1'>
          <div className='text-primary font-medium text-sm'>{label}</div>
          {required && <Asterisk size={10} color='#D92D20' weight='bold' className='mt-[2px]' />}
        </div>
      )}
      {selectionMode === 'multiple' ? (
        <SelectNextUI
          name={name}
          value={value}
          placeholder={placeholder}
          id={id}
          selectedKeys={selectedKeys}
          onChange={onChange}
          onSelectionChange={onSelectionChange}
          isInvalid={isInvalid}
          errorMessage={errorMessage}
          variant='bordered'
          size={size}
          selectionMode={selectionMode}
          radius={radius}
          renderValue={(items) => {
            return (
              <div className='flex flex-wrap gap-2'>
                {items?.map((item) => {
                  return (
                    <div
                      key={item?.key}
                      className='border-1 flex items-center gap-1 max-h-6 border-borderPrimary rounded-sm py-1 px-2'
                    >
                      <Text type='font-14-500'>{item?.textValue}</Text>

                      <X
                        onClick={() => {
                          const selectedKeysArray = Array.isArray(selectedKeys)
                            ? selectedKeys
                            : Array.from(selectedKeys);
                          const newSelectedKeys = selectedKeysArray.filter(
                            (key: any) => key !== item.key,
                          );

                          onSelectionChange?.(newSelectedKeys);
                        }}
                        size={12}
                        className='hover:cursor-pointer hover:text-red-500'
                      />
                    </div>
                  );
                })}
              </div>
            );
          }}
          classNames={{
            selectorIcon: ['w-5 h-5 !text-tertiary'],
            trigger: cn(
              'bg-white shadow-none border-1 border-borderPrimary rounded-md',
              'hover:!border-border-brand group-data-[focus=true]:!border-border-brand',
              'data-[open=true]:!border-border-brand',
            ),
            innerWrapper: '[&>span]:!text-text-placeholder',
            errorMessage: 'text-text-error-primary',
          }}
          {...props}
        >
          {options?.map((item) => {
            return (
              <SelectItem
                classNames={{
                  selectedIcon: ['text-bg-brand-solid'],
                  base: [
                    'data-[selectable=true]:focus:!bg-bg-secondary data-[focus-visible=true]:!outline-none',
                  ],
                  title: ['font-semibold text-primary'],
                  description: ['text-xs text-gray-500'],
                }}
                key={item?.value}
                value={item?.value}
                description={item?.description}
              >
                {item?.label}
              </SelectItem>
            );
          })}
        </SelectNextUI>
      ) : (
        <SelectNextUI
          variant='bordered'
          name={name}
          value={value}
          id={id}
          placeholder={placeholder}
          selectedKeys={selectedKeys}
          onChange={onChange}
          onSelectionChange={onSelectionChange}
          isInvalid={isInvalid}
          errorMessage={errorMessage}
          size={size}
          radius={radius}
          classNames={{
            selectorIcon: ['w-5 h-5 !text-tertiary'],
            trigger: cn(
              'bg-white shadow-none border-1 border-borderPrimary rounded-md',
              'hover:!border-border-brand group-data-[focus=true]:!border-border-brand',
              'data-[open=true]:!border-border-brand',
            ),
            innerWrapper: '[&>span]:!text-text-placeholder',
            errorMessage: 'text-text-error-primary',
          }}
          {...props}
        >
          {options?.map((item) => {
            return (
              <SelectItem
                classNames={{
                  selectedIcon: ['text-bg-brand-solid'],
                  base: [
                    'data-[selectable=true]:focus:!bg-bg-secondary data-[focus-visible=true]:!outline-none',
                  ],
                  title: ['font-semibold text-primary'],
                  description: ['text-xs text-gray-500'],
                }}
                key={item?.value}
                value={item?.value}
                description={item?.description}
              >
                  {item?.label}
              </SelectItem>
            );
          })}
        </SelectNextUI>
      )}
    </div>
  );
};

export default Select;
